/**
 * Attach each concept's stored image (spec 83) to the scoring results.
 *
 * Matched by the submission index the service returns, never by brand or headline: those repeat
 * (DEV sim 6059 has two "Archer / Butcher Strips"). A URL the service itself returned is kept.
 */

interface ImageCarrier {
  product_image_url?: string;
}
interface ScoredConcept extends ImageCarrier {
  index?: number;
  concept_str?: string;
  brand_name?: string;
  overall_mean?: number;
}
interface RankedConcept extends ImageCarrier {
  concept_str?: string;
  brand_name?: string;
  overall_mean?: number;
}

const SAME_SCORE_TOLERANCE = 0.001;

const storedUrls = (contentData: unknown): Array<string | null> => {
  const concepts = (contentData as { concepts?: unknown })?.concepts;
  if (!Array.isArray(concepts)) return [];
  return concepts.map((c) => {
    const url = (c as ImageCarrier | null)?.product_image_url;
    return typeof url === 'string' && url ? url : null;
  });
};

const sameScore = (a?: number, b?: number) =>
  typeof a === 'number' && typeof b === 'number' && Math.abs(a - b) < SAME_SCORE_TOLERANCE;

const conceptForRanking = <C extends ScoredConcept>(
  row: RankedConcept,
  concepts: C[],
): C | undefined => {
  const sameText = row.concept_str ? concepts.filter((c) => c.concept_str === row.concept_str) : [];
  if (sameText.length)
    return sameText.find((c) => sameScore(c.overall_mean, row.overall_mean)) ?? sameText[0];
  return concepts.find(
    (c) => c.brand_name === row.brand_name && sameScore(c.overall_mean, row.overall_mean),
  );
};

const withUrl = <T extends ImageCarrier>(item: T, url: string | null | undefined): T =>
  url && !item.product_image_url ? { ...item, product_image_url: url } : item;

export function withMultiConceptImages<
  C extends ScoredConcept,
  R extends RankedConcept,
  T extends { concepts: C[]; ranking: R[] },
>(run: T, contentData: unknown): T {
  const urls = storedUrls(contentData);
  const concepts = run.concepts.map((c) =>
    withUrl(c, typeof c.index === 'number' ? urls[c.index] : null),
  );
  const ranking = run.ranking.map((row) =>
    withUrl(row, conceptForRanking(row, concepts)?.product_image_url),
  );
  return { ...run, concepts, ranking };
}

export const hasConceptImages = (run: { concepts: ImageCarrier[] }): boolean =>
  run.concepts.some((c) => !!c.product_image_url);

export function withSingleConceptImage<T extends { concept?: ImageCarrier }>(
  report: T,
  contentData: unknown,
): T {
  const url = (contentData as ImageCarrier | null)?.product_image_url;
  if (!url || report.concept?.product_image_url) return report;
  return { ...report, concept: { ...report.concept, product_image_url: url } };
}
